import { mockState } from '@/api/mock-data'
import { USE_MOCK } from '@/config'
import type { Company } from '@/types/company'
import type { CompanyType } from '@/types/enums'
import { createClient } from '@/api/supabase'
import i18n from '@/utils/i18n'

export interface CompanyListParams {
  page?: number
  pageSize?: number
  type?: CompanyType | 'all'
  search?: string
  includeInactive?: boolean
}

export interface CompanyListResult {
  items: Company[]
  total: number
}

function mockList(p: CompanyListParams): CompanyListResult {
  const page = Math.max(1, p.page ?? 1)
  const pageSize = Math.min(100, p.pageSize ?? 25)
  let rows = [...mockState.companies]
  if (!p.includeInactive) rows = rows.filter((c) => c.is_active)
  if (p.type && p.type !== 'all') {
    rows = rows.filter((c) => c.type === p.type)
  }
  if (p.search?.trim()) {
    const q = p.search.trim().toLowerCase()
    rows = rows.filter((c) => c.name.toLowerCase().includes(q))
  }
  rows.sort((a, b) => a.name.localeCompare(b.name, 'ru'))
  const total = rows.length
  const start = (page - 1) * pageSize
  return { items: rows.slice(start, start + pageSize), total }
}

export async function listCompanies(
  params: CompanyListParams,
): Promise<CompanyListResult> {
  if (USE_MOCK) return mockList(params)

  const supabase = createClient()
  const page = Math.max(1, params.page ?? 1)
  const pageSize = Math.min(100, params.pageSize ?? 25)
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  let query = supabase.from('companies').select('*', { count: 'exact' })

  if (!params.includeInactive) {
    query = query.eq('is_active', true)
  }
  if (params.type && params.type !== 'all') {
    query = query.eq('type', params.type)
  }
  if (params.search?.trim()) {
    query = query.ilike('name', `%${params.search.trim()}%`)
  }

  const { data, error, count } = await query
    .order('name', { ascending: true })
    .range(from, to)

  if (error) throw new Error(error.message)
  return { items: (data ?? []) as Company[], total: count ?? 0 }
}

export async function getCompany(id: string): Promise<Company | null> {
  if (USE_MOCK) {
    return mockState.companies.find((c) => c.id === id) ?? null
  }

  const supabase = createClient()
  const { data, error } = await supabase
    .from('companies')
    .select('*')
    .eq('id', id)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw new Error(error.message)
  }
  return data as Company
}

export function nameDuplicateWarning(
  name: string,
  excludeCompanyId?: string,
): boolean {
  const n = name.trim().toLowerCase()
  return mockState.companies.some(
    (c) =>
      c.id !== excludeCompanyId && c.name.trim().toLowerCase() === n,
  )
}

export async function createCompany(
  data: Omit<Company, 'id' | 'created_at' | 'updated_at'>,
): Promise<Company> {
  if (USE_MOCK) {
    const now = new Date().toISOString()
    const c: Company = {
      ...data,
      id: `company-${crypto.randomUUID().slice(0, 8)}`,
      created_at: now,
      updated_at: now,
    }
    mockState.companies.push(c)
    return c
  }

  const supabase = createClient()
  const { data: created, error } = await supabase
    .from('companies')
    .insert(data)
    .select()
    .single()

  if (error) throw new Error(error.message)
  return created as Company
}

export async function updateCompany(
  id: string,
  data: Partial<Company>,
): Promise<Company> {
  if (USE_MOCK) {
    const i = mockState.companies.findIndex((c) => c.id === id)
    if (i < 0) throw new Error(i18n.t('apiErr.companies.notFound'))
    const now = new Date().toISOString()
    mockState.companies[i] = { ...mockState.companies[i], ...data, id, updated_at: now }
    return mockState.companies[i]
  }

  const supabase = createClient()
  const { data: updated, error } = await supabase
    .from('companies')
    .update({ ...data, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()

  if (error) throw new Error(error.message)
  return updated as Company
}

/**
 * Bulk-счётчик активных пользователей по компаниям. Используется в
 * `CompanyTable` (колонка «Пользователей»). Раньше строки таблицы тянулись
 * через `mockState.profiles` → в реальном режиме всегда рисовалось 0.
 */
export async function listCompanyUserCounts(
  companyIds: string[],
): Promise<Record<string, number>> {
  const out: Record<string, number> = {}
  for (const id of companyIds) out[id] = 0
  if (companyIds.length === 0) return out

  if (USE_MOCK) {
    for (const p of mockState.profiles) {
      if (!p.is_active) continue
      if (p.company_id && out[p.company_id] != null) {
        out[p.company_id] += 1
      }
    }
    return out
  }

  const supabase = createClient()
  const { data, error } = await supabase
    .from('profiles')
    .select('company_id')
    .eq('is_active', true)
    .in('company_id', companyIds)
  if (error) throw new Error(error.message)
  for (const row of (data ?? []) as Array<{ company_id: string | null }>) {
    const cid = row.company_id
    if (cid && out[cid] != null) out[cid] += 1
  }
  return out
}

export async function deactivateCompany(id: string): Promise<void> {
  if (USE_MOCK) {
    const openOrders = mockState.orders.filter(
      (o) =>
        (o.customer_company_id === id || o.contractor_company_id === id) &&
        !['COMPLETED', 'CANCELLED', 'REFUSED_BY_INSPECTOR', 'REFUSED_BY_SELLER', 'REFUSED_BUYOUT'].includes(o.status),
    )
    if (openOrders.length) {
      throw new Error(i18n.t('apiErr.companies.hasUnfinishedOrders'))
    }
    await updateCompany(id, { is_active: false, status: 'blocked' })
    mockState.profiles
      .filter((p) => p.company_id === id)
      .forEach((p) => {
        p.is_active = false
        p.status = 'blocked'
        p.updated_at = new Date().toISOString()
      })
    return
  }

  const supabase = createClient()
  const { error } = await supabase.rpc('deactivate_company', { p_company_id: id })
  if (error) throw new Error(error.message)
}

/**
 * Soft-delete компании (выставляет `deleted_at = now()`).
 * RPC из миграции `20260505120000_status_sync_rpcs` репо `stryzh`.
 */
export async function softDeleteCompany(id: string): Promise<void> {
  if (USE_MOCK) {
    await updateCompany(id, {
      is_active: false,
      deleted_at: new Date().toISOString(),
    })
    return
  }
  const supabase = createClient()
  const { error } = await supabase.rpc('soft_delete_company', { p_company_id: id })
  if (error) throw new Error(error.message)
}

const COMPANY_ASSETS_BUCKET = 'company-assets'

/** Тип ассета компании в bucket `company-assets`. Путь: `{companyId}/{kind}.{ext}`. */
export type CompanyAssetKind = 'facsimile' | 'seal'

/**
 * Загрузка факсимиле/печати в приватный bucket `company-assets`.
 * Возвращает путь объекта; для отображения нужен `getCompanyAssetSignedUrl`.
 * Bucket из миграции `20260505130000` репо `stryzh`.
 */
export async function uploadCompanyAsset(
  companyId: string,
  kind: CompanyAssetKind,
  file: File,
): Promise<string> {
  if (USE_MOCK) {
    return URL.createObjectURL(file)
  }

  const ext = file.name.split('.').pop()?.toLowerCase() ?? 'png'
  const safeExt = ext.replace(/[^a-z0-9]/g, '') || 'png'
  const path = `${companyId}/${kind}.${safeExt}`
  const supabase = createClient()
  const { error } = await supabase.storage
    .from(COMPANY_ASSETS_BUCKET)
    .upload(path, file, {
      upsert: true,
      contentType: file.type || 'image/png',
      cacheControl: '3600',
    })
  if (error) throw new Error(error.message)
  return path
}

/** Signed URL на 1 час (bucket приватный). */
export async function getCompanyAssetSignedUrl(
  path: string,
  expiresInSec = 3600,
): Promise<string | null> {
  if (USE_MOCK) return path
  if (!path) return null
  const supabase = createClient()
  const { data, error } = await supabase.storage
    .from(COMPANY_ASSETS_BUCKET)
    .createSignedUrl(path, expiresInSec)
  if (error || !data?.signedUrl) return null
  return data.signedUrl
}

// ──────────────────────────────────────────────────────────────────────────
// Шаблон ДКП компании (bucket `dkp-templates`).
// Для автогенерации: pdfgen берёт шаблон из этого bucket-а по company.dkp_template_path.
// ──────────────────────────────────────────────────────────────────────────

const DKP_TEMPLATES_BUCKET = 'dkp-templates'

/** Загрузить шаблон ДКП компании. Перезаписывает предыдущий по тому же пути. */
export async function uploadCompanyDkpTemplate(
  companyId: string,
  file: File,
): Promise<string> {
  if (!file.name.toLowerCase().endsWith('.docx')) {
    throw new Error(i18n.t('apiErr.companies.templateMustBeDocx'))
  }

  const path = `${companyId}/template.docx`

  if (USE_MOCK) {
    const i = mockState.companies.findIndex((c) => c.id === companyId)
    if (i >= 0) {
      mockState.companies[i] = {
        ...mockState.companies[i],
        dkp_template_path: path,
        dkp_template_uploaded_at: new Date().toISOString(),
      }
    }
    return path
  }

  const supabase = createClient()
  const { error: upErr } = await supabase.storage
    .from(DKP_TEMPLATES_BUCKET)
    .upload(path, file, {
      upsert: true,
      contentType:
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      cacheControl: '0',
    })
  if (upErr) throw new Error(upErr.message)

  const { data: userData } = await supabase.auth.getUser()
  const { error: updErr } = await supabase
    .from('companies')
    .update({
      dkp_template_path: path,
      dkp_template_uploaded_at: new Date().toISOString(),
      dkp_template_uploaded_by: userData?.user?.id ?? null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', companyId)
  if (updErr) throw new Error(updErr.message)

  return path
}

/** Signed URL для скачивания шаблона. Bucket приватный. */
export async function getCompanyDkpTemplateSignedUrl(
  path: string,
  expiresInSec = 600,
): Promise<string | null> {
  if (USE_MOCK) return path || null
  if (!path) return null
  const supabase = createClient()
  const { data, error } = await supabase.storage
    .from(DKP_TEMPLATES_BUCKET)
    .createSignedUrl(path, expiresInSec)
  if (error || !data?.signedUrl) return null
  return data.signedUrl
}

/** Удалить шаблон и обнулить ссылку в companies. */
export async function deleteCompanyDkpTemplate(
  companyId: string,
  path: string,
): Promise<void> {
  if (USE_MOCK) {
    const i = mockState.companies.findIndex((c) => c.id === companyId)
    if (i >= 0) {
      mockState.companies[i] = {
        ...mockState.companies[i],
        dkp_template_path: null,
        dkp_template_uploaded_at: null,
      }
    }
    return
  }
  const supabase = createClient()
  if (path) {
    await supabase.storage.from(DKP_TEMPLATES_BUCKET).remove([path])
  }
  const { error } = await supabase
    .from('companies')
    .update({
      dkp_template_path: null,
      dkp_template_uploaded_at: null,
      dkp_template_uploaded_by: null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', companyId)
  if (error) throw new Error(error.message)
}
